import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
const heroImages = [{
  src: 'https://images.unsplash.com/photo-1587745416684-47953f16f02f?auto=format&fit=crop&q=80&w=1200',
  label: 'Modern Fleet'
}, {
  src: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?auto=format&fit=crop&q=80&w=1200',
  label: 'Wheelchair Accessible'
}, {
  src: 'https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&q=80&w=1200',
  label: 'Compassionate Care'
}];
export function Hero() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  // Auto-cycling gallery logic
  useEffect(() => {
    const intervalTime = isHovered ? 2000 : 4000;
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % heroImages.length);
    }, intervalTime);
    return () => clearInterval(timer);
  }, [isHovered]);
  return <section className="relative overflow-hidden bg-brand-light pt-10 pb-16 lg:pt-16 lg:pb-24" data-id="element-119">
      <div className="container mx-auto px-4 md:px-6" data-id="element-120">
        <div className="grid lg:grid-cols-2 gap-8 xl:gap-12 items-center" data-id="element-121">
          {/* Left Column: Text & CTAs */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-xl xl:max-w-xl relative z-20" data-id="element-122">
            <h1 className="text-3xl sm:text-4xl md:text-4xl xl:text-5xl font-extrabold text-brand-blue leading-[1.1] mb-4 tracking-tight" data-id="element-123">
              Elevating Non-emergency Medical Transportation
            </h1>
            <p className="text-base text-gray-800 mb-6 leading-relaxed max-w-lg" data-id="element-124">
              Our modern fleet of over 90 vehicles is more than just
              transportation. We provide compassionate, reliable care for every
              patient.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3" data-id="element-125">
              <Link to="/services" className="group flex items-center justify-center gap-2 bg-brand-red text-white px-5 py-2.5 rounded-full font-bold text-sm hover:opacity-90 transition-all shadow-lg shadow-red-900/20 focus-visible:ring-4 focus-visible:ring-red-400 outline-none w-full sm:w-auto" aria-label="Request a medical transport ride" data-id="element-126">
                Request a Ride
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-127" />
              </Link>
              <Link to="/employees" className="flex items-center justify-center gap-2 bg-white text-brand-blue border-2 border-brand-blue px-5 py-2.5 rounded-full font-bold text-sm hover:bg-brand-light transition-all focus-visible:ring-4 focus-visible:ring-blue-400 outline-none w-full sm:w-auto" aria-label="Apply to drive with MediRide USA" data-id="element-128">
                Drive with Us
              </Link>
            </div>
          </motion.div>

          {/* Right Column: Stacked Image Reveal */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative w-full max-w-lg mx-auto lg:max-w-none mt-8 lg:mt-0" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-129">
            {/* Stylized background shape */}
            <div className="absolute -top-10 -right-10 w-full h-full bg-blue-500 rounded-[100px] rotate-12 opacity-10 blur-3xl pointer-events-none" data-id="element-130" />

            {/* Image Stack Container */}
            <div className="relative z-10 aspect-video w-full" data-id="element-131">
              {heroImages.map((img, i) => {
              // Calculate position in the stack relative to the current index
              const offset = (i - currentIndex + heroImages.length) % heroImages.length;
              const isFront = offset === 0;
              const isBack = offset === 1;
              // Determine z-index to ensure correct layering
              const zIndex = isFront ? 30 : isBack ? 20 : 10;
              return <motion.div key={i} className="absolute inset-0 rounded-[40px] lg:rounded-[80px] overflow-hidden shadow-2xl bg-white" initial={false} animate={{
                // Front image slides top-left, Back image slides bottom-right
                x: isFront ? isHovered ? '-4%' : '0%' : isBack ? isHovered ? '12%' : '4%' : '4%',
                y: isFront ? isHovered ? '-4%' : '0%' : isBack ? isHovered ? '12%' : '4%' : '4%',
                scale: isFront ? 1 : isBack ? isHovered ? 0.98 : 0.95 : 0.9,
                opacity: isFront ? isHovered ? 0.9 : 1 : isBack ? 1 : 0,
                zIndex: zIndex
              }} transition={{
                duration: isHovered ? 0.6 : 0.4,
                ease: [0.25, 0.1, 0.25, 1],
                opacity: {
                  // 1-second delay before the front image fades on hover
                  delay: isHovered && isFront ? 1 : 0,
                  duration: 0.4
                }
              }} data-id="element-132">
                    <img src={img.src} alt={img.label} className="w-full h-full object-cover" data-id="element-133" />
                    {/* Subtle overlay to enhance text readability and premium feel */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-brand-blue/20 to-transparent pointer-events-none" data-id="element-134" />

                    {/* Hover Reveal Label on the Back Image */}
                    <motion.div initial={false} animate={{
                  opacity: isBack && isHovered ? 1 : 0,
                  y: isBack && isHovered ? 0 : 10
                }} transition={{
                  duration: 0.4,
                  delay: 0.1
                }} className="absolute bottom-8 right-8 bg-white/95 backdrop-blur-md text-brand-blue px-6 py-3 rounded-2xl font-bold shadow-xl border border-white/20" data-id="element-135">
                      {img.label}
                    </motion.div>
                  </motion.div>;
            })}
            </div>

            {/* Floating Badge - Remains Fixed */}
            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl z-40 hidden md:block pointer-events-none" data-id="element-136">
              <div className="flex items-center gap-4" data-id="element-137">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600" data-id="element-138">
                  <span className="font-bold text-xl" data-id="element-139">90+</span>
                </div>
                <div data-id="element-140">
                  <p className="font-bold text-brand-blue" data-id="element-141">Modern Vehicles</p>
                  <p className="text-sm text-gray-500" data-id="element-142">Serving our community</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Wave Divider */}
      <div className="absolute bottom-0 left-0 w-full leading-[0] overflow-hidden pointer-events-none z-10" data-id="element-143">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-[40px] fill-white" data-id="element-144">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C58,47.26,163.78,85.54,321.39,56.44Z" data-id="element-145"></path>
        </svg>
      </div>
    </section>;
}